<script setup lang="ts">
import { computed, toRef } from "vue";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import { useCardLocalOptions } from "@/composables/useCardLocalOptions";
import { resolveLocalizedTitle } from "@/composables/useCardData";
import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
import { Select } from "@/components/episto-ui/select";
import type { ChartCard } from "@/types";
import ColorToggleField from "./CardOptionFields/ColorToggleField.vue";
import MultipleChoiceQuestionField from "./CardOptionFields/MultipleChoiceQuestionField.vue";
import PopulationField from "./CardOptionFields/PopulationField.vue";
import { cardCodes, isMultipleChoiceCard, useAnalysisContext, useCardPopulation } from "./analysisData";
import { TURF_COMBO_SIZES, TURF_DEFAULT_COMBO_SIZE } from "./constants";
import OptionRow from "./CardOptionFields/OptionRow.vue";
import OptionSection from "./CardOptionFields/OptionSection.vue";

const props = defineProps({
  card: { type: Object, required: true },
});

const card = toRef(props, "card");
const cardsStore = useResultsV2CardsStore();
const { availableCards, displayLocale } = useAnalysisContext();
const { localOptions, updateLocalOption } = useCardLocalOptions(card);
const { color: populationColor } = useCardPopulation(card);

const resolveTitle = (title: unknown) => removeHtmlMarkdownAndLineBreak(resolveLocalizedTitle(title, displayLocale.value));

function setContent(patch: Record<string, unknown>) {
  cardsStore.updateCardLocal(props.card.id, { content: { ...props.card.content, ...patch } });
}

const questionId = computed<number | null>(() => props.card.content?.question_id || null);

const questionMeta = computed<ChartCard | null>(
  () =>
    (availableCards.value as ChartCard[]).find((c) => isMultipleChoiceCard(c) && (c.questions || []).some((q) => q.id === questionId.value)) ||
    null,
);

// An exclusive answer never combines with another: it would only waste a slot.
const exclusiveCodes = (meta: ChartCard | null) =>
  (meta?.specs?.answers || []).flatMap((answer, idx) => (answer.exclusive ? [String(answer.code || idx + 1)] : []));

const contentOnPick = (meta: ChartCard | null) => ({ forced_codes: [], excluded_codes: exclusiveCodes(meta) });

const answerOptions = computed(() =>
  questionMeta.value ? cardCodes(questionMeta.value, resolveTitle).map(({ code, label }) => ({ value: code, label })) : [],
);

const SIZE_OPTIONS = TURF_COMBO_SIZES.map((size) => ({ value: size, label: `Best combination of ${size}` }));

const comboSize = computed({
  get: () => props.card.content?.combo_size ?? TURF_DEFAULT_COMBO_SIZE,
  set: (value: number) => setContent({ combo_size: value }),
});

const forcedCodes = computed<string[]>(() => props.card.content?.forced_codes || []);
const excludedCodes = computed<string[]>(() => props.card.content?.excluded_codes || []);

// An answer is either forced or excluded, never both.
const setForced = (codes: string[] | null) =>
  setContent({ forced_codes: codes || [], excluded_codes: excludedCodes.value.filter((code) => !(codes || []).includes(code)) });
const setExcluded = (codes: string[] | null) =>
  setContent({ excluded_codes: codes || [], forced_codes: forcedCodes.value.filter((code) => !(codes || []).includes(code)) });

const accentColor = computed({
  get: () => localOptions.value.color || null,
  set: (value: string | null) => updateLocalOption("color", value),
});
</script>

<template>
  <OptionSection
    id="data"
    title="Data">
    <MultipleChoiceQuestionField
      :card="card"
      :content-on-pick="contentOnPick" />

    <OptionRow
      label="Portfolio size"
      hint="How many answers you can actually launch or keep: the business constraint the search works within.">
      <Select
        :model-value="comboSize"
        :options="SIZE_OPTIONS"
        @update:model-value="comboSize = Number($event)" />
    </OptionRow>

    <template v-if="answerOptions.length">
      <OptionRow
        label="Always included"
        hint="Answers that stay in every combination, such as the current offer that will not be retired.">
        <Select
          :model-value="forcedCodes"
          :options="answerOptions"
          placeholder="None"
          multiple
          filterable
          @update:model-value="setForced($event as string[] | null)" />
      </OptionRow>

      <OptionRow
        label="Excluded"
        hint="Answers that cannot be part of the portfolio: 'Other', 'None of these'. Exclusive answers are excluded when the question is picked.">
        <Select
          :model-value="excludedCodes"
          :options="answerOptions"
          placeholder="None"
          multiple
          filterable
          @update:model-value="setExcluded($event as string[] | null)" />
      </OptionRow>
    </template>

    <PopulationField :card="card" />
  </OptionSection>

  <OptionSection
    id="display"
    title="Display"
    :count="accentColor ? 1 : 0">
    <ColorToggleField
      v-model="accentColor"
      :default-color="populationColor"
      hint="Color of the bars. It starts as the color of its population." />
  </OptionSection>
</template>
